import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import i18n, { loadLocale } from '../i18n';
import logger from '../utils/logger';

export type Language = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ja';

export interface LanguageConfig {
  code: Language;
  name: string;
  nativeName: string;
  flag: string;
}

export const LANGUAGES: Record<Language, LanguageConfig> = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸'
  },
  es: {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸'
  },
  fr: {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷'
  },
  de: {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪'
  },
  it: {
    code: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    flag: '🇮🇹'
  },
  pt: {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    flag: '🇵🇹'
  },
  ja: {
    code: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵'
  }
};

interface LocalizationContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  languages: LanguageConfig[];
  loading: boolean;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};

interface LocalizationProviderProps {
  children: ReactNode;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const detectLanguage = async () => {
      try {
        // Wait for i18n to be initialized
        if (!i18n.isInitialized) {
          await new Promise<void>((resolve) => {
            const checkInit = () => {
              if (i18n.isInitialized) {
                resolve();
              } else {
                setTimeout(checkInit, 100);
              }
            };
            checkInit();
          });
        }
        
        // Check for saved language preference
        const savedLanguage = localStorage.getItem('selectedLanguage') as Language;
        
        if (savedLanguage && LANGUAGES[savedLanguage]) {
          setLanguageState(savedLanguage);
          await loadLocale(savedLanguage);
          await i18n.changeLanguage(savedLanguage);
        } else {
          // Auto-detect based on browser language
          const browserLanguage = navigator.language.split('-')[0] as Language;
          const detectedLanguage = LANGUAGES[browserLanguage] ? browserLanguage : 'en';
          
          setLanguageState(detectedLanguage);
          localStorage.setItem('selectedLanguage', detectedLanguage);
          await loadLocale(detectedLanguage);
          await i18n.changeLanguage(detectedLanguage);
        }
      } catch (error) {
        console.error('❌ Error detecting language:', error);
        setLanguageState('en');
      } finally {
        setLoading(false);
      }
    };

    detectLanguage();
  }, []);

  const setLanguage = async (newLanguage: Language) => {
    logger.debug('🔄 LocalizationContext: Setting language to:', newLanguage);
    logger.debug('🔧 LocalizationContext: i18n initialized:', i18n.isInitialized);
    logger.debug('🌍 LocalizationContext: Current i18n language:', i18n.language);
    
    setLanguageState(newLanguage);
    localStorage.setItem('selectedLanguage', newLanguage);
    document.documentElement.lang = newLanguage;

    logger.debug('🌐 LocalizationContext: Changing i18n language to:', newLanguage);
    try {
      await loadLocale(newLanguage);
      await i18n.changeLanguage(newLanguage);
      logger.info('✅ LocalizationContext: i18n language changed to:', i18n.language);
    } catch (error) {
      logger.error('❌ LocalizationContext: Error changing i18n language:', error);
    }
  };

  const value = {
    language,
    setLanguage,
    languages: Object.values(LANGUAGES),
    loading,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
